// Saúde / Personal (pedido 2026-10-25, mockup "Seu plano de evolução") — aba
// Saúde do Assistente IA: treino do dia com progresso, passos/água/sono,
// exercícios para marcar, refeições do dia com calorias e macros, e a IA
// embaixo ("Pergunte ao seu personal…") que monta treino, registra refeição
// etc. Dados PESSOAIS do cliente (GET /ai-assistant/health).
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronRight, Droplet, Dumbbell, Footprints, Loader2, Moon, Play, Trash2, UtensilsCrossed, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { gpsDriveAiAssistantService, type AiHealth, type AiMe, type AiWorkoutExercise } from "@/services/gpsdrive-ai-assistant.service";
import { AssistantChat } from "./assistant-chat";

const TZ = "America/Sao_Paulo";
const todayYmd = () => new Date().toLocaleDateString("en-CA", { timeZone: TZ });
const WEEKDAYS = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];
const QUOTES = [
  "Disciplina hoje, resultados amanhã!",
  "Um passo de cada vez, todo dia.",
  "Feito é melhor que perfeito.",
  "Seu corpo agradece cada treino.",
  "Constância vence intensidade.",
  "Cuide de você: você é o seu maior projeto.",
  "Pouco todo dia é muito no fim do mês.",
];

const fmtInt = (n: number) => n.toLocaleString("pt-BR");
const liters = (ml: number) => (ml / 1000).toLocaleString("pt-BR", { maximumFractionDigits: 1 });
const sleepLabel = (min: number) => `${Math.floor(min / 60)}h${min % 60 ? ` ${String(min % 60).padStart(2, "0")}min` : ""}`;
const pct = (v: number, goal: number) => (goal > 0 ? Math.min(100, Math.round((v / goal) * 100)) : 0);

function exerciseEmoji(name: string) {
  const n = name.toLowerCase();
  if (/caminh|esteira/.test(n)) return "🚶";
  if (/corr/.test(n)) return "🏃";
  if (/bike|bicic|pedal/.test(n)) return "🚴";
  if (/nata|piscina/.test(n)) return "🏊";
  if (/prancha|abdom/.test(n)) return "🧘";
  if (/along|yoga|ioga/.test(n)) return "🤸";
  if (/agach|leg|perna|afundo|stiff/.test(n)) return "🦵";
  if (/supino|flex|peito|rosca|tr[ií]ceps|b[ií]ceps|ombro|remada|puxada/.test(n)) return "🏋️";
  return "💪";
}
const exerciseDetail = (e: AiWorkoutExercise) =>
  e.minutes ? `${e.minutes} min` : e.sets && e.reps ? `${e.sets} × ${e.reps}` : e.sets ? `${e.sets} séries` : (e.reps ?? "");

function mealEmoji(meal: string) {
  const n = meal.toLowerCase();
  if (/caf[eé]/.test(n)) return "☕";
  if (/almo/.test(n)) return "🍽️";
  if (/lanche/.test(n)) return "🥪";
  if (/jantar|janta/.test(n)) return "🍲";
  if (/ceia/.test(n)) return "🥛";
  return "🍴";
}

const HERO_FADE = "linear-gradient(to right, transparent 0%, #000 38%), linear-gradient(to bottom, #000 72%, transparent 100%)";
const HERO_MASK: React.CSSProperties = {
  WebkitMaskImage: HERO_FADE,
  maskImage: HERO_FADE,
  WebkitMaskComposite: "source-in",
  maskComposite: "intersect",
};

type Editing = { kind: "steps" | "sleep"; value: string } | null;

export function SmartHealth({ me, keyboardInset = 0 }: { me: AiMe; keyboardInset?: number }) {
  const qc = useQueryClient();
  const date = todayYmd();
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [prefill, setPrefill] = useState<{ text: string; nonce: number } | null>(null);
  const [waterOpen, setWaterOpen] = useState(false);
  const [editing, setEditing] = useState<Editing>(null);
  const [plansOpen, setPlansOpen] = useState(false);
  const [confirmMeal, setConfirmMeal] = useState<string | null>(null);
  const ask = (text: string) => setPrefill({ text, nonce: Date.now() });

  const key = ["ai-assistant", "health", date];
  const data = useQuery({ queryKey: key, queryFn: () => gpsDriveAiAssistantService.health(date) });
  const h = data.data;
  const apply = (next: AiHealth) => qc.setQueryData(key, next);
  const refresh = () => qc.invalidateQueries({ queryKey: ["ai-assistant", "health"] });

  const water = useMutation({ mutationFn: (ml: number) => gpsDriveAiAssistantService.addWater(date, ml), onSuccess: apply });
  const saveDay = useMutation({
    mutationFn: (input: { steps?: number | null; sleepMin?: number | null }) => gpsDriveAiAssistantService.setHealthDay({ date, ...input }),
    onSuccess: (next) => {
      apply(next);
      setEditing(null);
    },
  });
  const mark = useMutation({
    mutationFn: (input: { planId: string; index: number; done: boolean }) => gpsDriveAiAssistantService.markExercise({ date, ...input }),
    onSuccess: apply,
  });
  const removeMeal = useMutation({
    mutationFn: (id: string) => gpsDriveAiAssistantService.deleteMeal(id),
    onSuccess: () => {
      setConfirmMeal(null);
      refresh();
    },
  });
  const removeWorkout = useMutation({ mutationFn: (id: string) => gpsDriveAiAssistantService.deleteWorkout(id), onSuccess: refresh });

  const plan = h?.todayPlan ?? null;
  const doneCount = plan?.done.length ?? 0;
  const total = plan?.exercises.length ?? 0;
  const progress = total ? Math.round((doneCount / total) * 100) : 0;
  const nextIndex = plan ? plan.exercises.findIndex((_, i) => !plan.done.includes(i)) : -1;
  const quote = QUOTES[new Date(`${date}T12:00:00Z`).getUTCDate() % QUOTES.length]!;
  const goals = h?.goals;
  const macroGoal = (g: number | null | undefined, fallback: number) => g ?? fallback;

  const suggestions = [
    { label: "🏋️ Montar meu treino", message: "Monta um treino para mim. Meu objetivo é ", send: false },
    { label: "🍽️ Registrar refeição", message: "Almocei ", send: false },
    { label: "💧 Bebi água", message: "Bebi 1 copo de água", send: true },
    { label: "📊 Como estou hoje?", message: "Como estou hoje na minha saúde?", send: true },
    { label: "🥗 Plano alimentar", message: "Monta um plano alimentar para mim. Meu objetivo é ", send: false },
    { label: "🎯 Minhas metas", message: "Quero definir minhas metas: ", send: false },
  ];

  return (
    <div className="ai-chat-bg flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto pb-3">
        {/* Hero */}
        <section className="relative overflow-hidden bg-gradient-to-b from-white via-white/90 to-transparent dark:from-slate-900 dark:via-slate-900/90">
          {/* Ilustração some em degradê à esquerda e embaixo (máscara, sem
              borda reta por cima do fundo pontilhado). */}
          <img
            src="/ai-assistant/personal-hero.webp"
            alt=""
            aria-hidden
            className="pointer-events-none absolute right-0 top-0 h-[310px] w-auto max-w-[64%] object-cover object-left-top"
            style={HERO_MASK}
          />
          <div className="relative px-4 pt-4">
            <h2 className="max-w-[62%] text-[27px] font-extrabold leading-[1.05] tracking-tight text-slate-900 dark:text-white">
              Seu plano de evolução
            </h2>
            <p className="mt-1.5 max-w-[60%] text-[13px] text-slate-500 dark:text-slate-400">
              {goals?.goal ? `Objetivo: ${goals.goal}` : "Treine melhor. Cuide de você."}
            </p>
            <div className="mt-3 inline-block max-w-[58%] rounded-2xl ai-soft px-3 py-2 text-[12px] font-medium italic text-slate-700 dark:text-slate-200">
              “{quote}”
            </div>

            {/* Treino de hoje */}
            <div className="relative mt-3 w-[64%] min-w-[220px] rounded-3xl bg-[var(--ai-dark)] p-3.5 text-white shadow-[0_16px_30px_-14px_rgba(15,23,42,0.8)] ring-1 ring-white/10">
              <div className="flex items-start gap-2.5">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-white/15">
                  <Dumbbell className="h-5 w-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] font-bold uppercase tracking-widest opacity-80">Treino de hoje</div>
                  {plan ? (
                    <>
                      <div className="line-clamp-2 text-[15px] font-extrabold leading-tight">
                        {plan.name}
                        {plan.durationMin ? ` · ${plan.durationMin} min` : ""}
                      </div>
                      <div className="text-[11px] opacity-85">
                        {doneCount} de {total} exercícios
                      </div>
                    </>
                  ) : (
                    <div className="text-[14px] font-bold leading-snug">Dia livre — ou monte seu treino com a IA</div>
                  )}
                </div>
                {plan && (
                  <button type="button" aria-label="Ver treinos" onClick={() => setPlansOpen(true)} className="opacity-80">
                    <ChevronRight className="h-5 w-5" />
                  </button>
                )}
              </div>
              {plan && (
                <div className="mt-2 flex items-center gap-2">
                  <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/25">
                    <div className="h-full rounded-full bg-emerald-300" style={{ width: `${Math.max(3, progress)}%` }} />
                  </div>
                  <span className="text-[11px] font-bold tabular-nums">{progress}%</span>
                </div>
              )}
              <button
                type="button"
                onClick={() => {
                  if (!plan) ask("Monta um treino para mim. Meu objetivo é ");
                  else if (nextIndex >= 0) mark.mutate({ planId: plan.id, index: nextIndex, done: true });
                }}
                disabled={!!plan && nextIndex < 0}
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-emerald-300 py-2.5 text-[14px] font-extrabold text-emerald-950 shadow-md transition active:scale-95 disabled:opacity-90"
              >
                {!plan ? (
                  <>
                    <Dumbbell className="h-4 w-4" /> Montar meu treino
                  </>
                ) : nextIndex < 0 ? (
                  <>
                    <Check className="h-4 w-4" /> Treino concluído! 🎉
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 fill-current" /> {doneCount ? "Continuar treino" : "Começar treino"}
                  </>
                )}
              </button>
              {plan && nextIndex >= 0 && (
                <div className="mt-1.5 truncate text-center text-[10.5px] opacity-75">Próximo: {plan.exercises[nextIndex]!.name} (toque para marcar feito)</div>
              )}
            </div>
          </div>
        </section>

        <div className="space-y-4 px-4 pt-4">
          {data.isLoading && <Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" />}
          {data.error && <p className="rounded-2xl bg-red-50 p-3 text-sm text-red-700">Não foi possível carregar sua saúde.</p>}

          {/* Passos / Água / Sono */}
          <div className="grid grid-cols-3 gap-2.5">
            <StatCard
              icon={<Footprints className="h-5 w-5 text-emerald-600" />}
              iconBg="bg-emerald-50 dark:bg-emerald-950/40"
              label="Passos"
              value={h?.day.steps != null ? fmtInt(h.day.steps) : "—"}
              bar={pct(h?.day.steps ?? 0, goals?.stepsGoal ?? 10000)}
              barClass="bg-emerald-500"
              foot={`Meta: ${fmtInt(goals?.stepsGoal ?? 10000)}`}
              onClick={() => setEditing({ kind: "steps", value: h?.day.steps != null ? String(h.day.steps) : "" })}
            />
            <StatCard
              icon={<Droplet className="h-5 w-5 fill-blue-500 text-blue-500" />}
              iconBg="bg-blue-50 dark:bg-blue-950/40"
              label="Água"
              value={
                <>
                  {liters(h?.day.waterMl ?? 0)}
                  <span className="text-xs font-semibold text-slate-400"> / {liters(goals?.waterGoalMl ?? 2000)} L</span>
                </>
              }
              bar={pct(h?.day.waterMl ?? 0, goals?.waterGoalMl ?? 2000)}
              barClass="bg-blue-500"
              foot={`${pct(h?.day.waterMl ?? 0, goals?.waterGoalMl ?? 2000)}%`}
              onClick={() => setWaterOpen((o) => !o)}
            />
            <StatCard
              icon={<Moon className="h-5 w-5 fill-violet-500 text-violet-500" />}
              iconBg="bg-violet-50 dark:bg-violet-950/40"
              label="Sono"
              value={h?.day.sleepMin != null ? sleepLabel(h.day.sleepMin) : "—"}
              bar={pct(h?.day.sleepMin ?? 0, goals?.sleepGoalMin ?? 480)}
              barClass="bg-violet-500"
              foot={h?.day.sleepMin != null ? `${pct(h.day.sleepMin, goals?.sleepGoalMin ?? 480)}%` : "Toque p/ anotar"}
              onClick={() =>
                setEditing({ kind: "sleep", value: h?.day.sleepMin != null ? String(Math.round((h.day.sleepMin / 60) * 10) / 10).replace(".", ",") : "" })
              }
            />
          </div>

          {waterOpen && (
            <div className="flex flex-wrap items-center gap-2 rounded-2xl bg-white p-3 shadow-[0_8px_22px_-16px_rgba(15,23,42,0.55)] dark:bg-slate-900">
              <span className="text-xs font-semibold text-slate-500">Água:</span>
              {[
                ["+ 1 copo", 250],
                ["+ 500 ml", 500],
                ["+ 1 L", 1000],
                ["− 1 copo", -250],
              ].map(([label, ml]) => (
                <button
                  key={label}
                  type="button"
                  disabled={water.isPending}
                  onClick={() => water.mutate(ml as number)}
                  className={cn(
                    "rounded-full px-3 py-1.5 text-xs font-bold transition active:scale-95",
                    (ml as number) > 0 ? "bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300" : "text-slate-500",
                  )}
                >
                  {label}
                </button>
              ))}
              <button type="button" aria-label="Fechar" onClick={() => setWaterOpen(false)} className="ml-auto text-slate-400">
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Seu treino */}
          <div>
            <div className="mb-2 flex items-center justify-between px-1">
              <h4 className="text-[17px] font-extrabold text-slate-900 dark:text-white">Seu treino</h4>
              <button type="button" onClick={() => setPlansOpen(true)} className="flex items-center text-[13px] font-semibold ai-text">
                Ver plano completo <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            {plan ? (
              <div className="-mx-4 flex gap-2.5 overflow-x-auto px-4 pb-1 [scrollbar-width:none]">
                {plan.exercises.map((e, i) => {
                  const done = plan.done.includes(i);
                  return (
                    <button
                      key={`${e.name}-${i}`}
                      type="button"
                      onClick={() => mark.mutate({ planId: plan.id, index: i, done: !done })}
                      aria-pressed={done}
                      className={cn(
                        "flex w-[150px] shrink-0 items-center gap-2 rounded-2xl bg-white p-3 text-left shadow-[0_8px_22px_-16px_rgba(15,23,42,0.55)] transition active:scale-95 dark:bg-slate-900",
                        done && "ring-1 ring-emerald-300 dark:ring-emerald-800",
                      )}
                    >
                      <span className="text-[28px] leading-none">{exerciseEmoji(e.name)}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13px] font-semibold text-slate-800 dark:text-slate-100">{e.name}</span>
                        <span className="block text-[13px] font-bold text-slate-900 dark:text-white">{exerciseDetail(e)}</span>
                      </span>
                      <span
                        className={cn(
                          "grid h-6 w-6 shrink-0 place-items-center rounded-full border-2",
                          done ? "border-emerald-500 bg-emerald-500 text-white" : "border-slate-300 dark:border-slate-600",
                        )}
                      >
                        {done && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <EmptyCard
                emoji="🏋️"
                title={h?.plans.length ? "Hoje não tem treino marcado" : "Você ainda não tem treino"}
                text="Peça ao personal: “monta um treino de 3 dias para ganhar massa”."
                action="Montar meu treino"
                onAction={() => ask("Monta um treino para mim. Meu objetivo é ")}
              />
            )}
          </div>

          {/* Nutrição do dia */}
          <div>
            <div className="mb-2 flex items-center justify-between px-1">
              <h4 className="text-[17px] font-extrabold text-slate-900 dark:text-white">Nutrição do dia</h4>
              <button
                type="button"
                onClick={() => ask("Monta um plano alimentar para mim. Meu objetivo é ")}
                className="flex items-center text-[13px] font-semibold ai-text"
              >
                Ver plano alimentar <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-[1fr_auto] gap-2.5">
              <div className="space-y-2.5">
                {h?.meals.length ? (
                  h.meals.map((m) => (
                    <div key={m.id} className="rounded-2xl bg-white p-3 shadow-[0_8px_22px_-16px_rgba(15,23,42,0.55)] dark:bg-slate-900">
                      <div className="flex gap-2.5">
                        <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-amber-50 text-2xl dark:bg-amber-950/30">{mealEmoji(m.meal)}</span>
                        <div className="min-w-0 flex-1">
                          <div className="font-bold text-slate-900 dark:text-white">{m.meal}</div>
                          <div className="line-clamp-2 text-[12.5px] text-slate-500">{m.description}</div>
                          <div className="mt-1.5 flex items-center gap-2">
                            {m.kcal != null && (
                              <span className="inline-flex items-center gap-1 rounded-full ai-soft px-2 py-0.5 text-[11px] font-bold ai-text">
                                <UtensilsCrossed className="h-3 w-3" /> {m.kcal} kcal
                              </span>
                            )}
                            {confirmMeal === m.id ? (
                              <>
                                <button type="button" onClick={() => removeMeal.mutate(m.id)} className="rounded-full bg-red-600 px-2.5 py-0.5 text-[11px] font-bold text-white">
                                  Excluir
                                </button>
                                <button type="button" onClick={() => setConfirmMeal(null)} className="text-[11px] text-slate-500">
                                  Voltar
                                </button>
                              </>
                            ) : (
                              <button type="button" aria-label="Excluir refeição" onClick={() => setConfirmMeal(m.id)} className="ml-auto text-slate-300 hover:text-red-500">
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <EmptyCard
                    emoji="🥗"
                    title="Nenhuma refeição hoje"
                    text="Conte o que comeu (ou mande foto do prato) que o personal calcula."
                    action="Registrar refeição"
                    onAction={() => ask("Almocei ")}
                  />
                )}
              </div>
              <div className="w-[128px] self-start rounded-2xl bg-white p-3 shadow-[0_8px_22px_-16px_rgba(15,23,42,0.55)] dark:bg-slate-900">
                <div className="mb-2 text-[11px] font-semibold text-slate-500">
                  <span className="text-[15px] font-extrabold text-slate-900 dark:text-white">{fmtInt(h?.totals.kcal ?? 0)}</span>
                  {goals?.kcalGoal ? ` / ${fmtInt(goals.kcalGoal)}` : ""} kcal
                </div>
                <Macro label="Proteínas" value={h?.totals.proteinG ?? 0} goal={macroGoal(goals?.proteinGoalG, 120)} color="bg-emerald-500" />
                <Macro label="Carboidratos" value={h?.totals.carbsG ?? 0} goal={macroGoal(goals?.carbsGoalG, 250)} color="bg-amber-400" />
                <Macro label="Gorduras" value={h?.totals.fatG ?? 0} goal={macroGoal(goals?.fatGoalG, 65)} color="bg-blue-500" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Personal (texto ou voz) */}
      <div className="border-t ai-border bg-white/95 dark:border-slate-800 dark:bg-slate-950/95">
        <AssistantChat
          me={me}
          compact
          conversationId={conversationId}
          onConversationChange={setConversationId}
          suggestions={suggestions}
          prefill={prefill}
          placeholder="Pergunte ao seu personal…"
          bottomInset={keyboardInset}
          compactBottom
        />
      </div>

      {editing && (
        <div className="absolute inset-0 z-50 flex flex-col bg-black/40" onClick={() => setEditing(null)}>
          <form
            className="mt-auto rounded-t-3xl bg-white p-5 dark:bg-slate-900"
            style={{ paddingBottom: "calc(1.25rem + env(safe-area-inset-bottom))" }}
            onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => {
              e.preventDefault();
              const n = Number(editing.value.replace(/\./g, "").replace(",", "."));
              if (editing.value.trim() === "") saveDay.mutate(editing.kind === "steps" ? { steps: null } : { sleepMin: null });
              else if (Number.isFinite(n) && n >= 0)
                saveDay.mutate(editing.kind === "steps" ? { steps: Math.round(n) } : { sleepMin: Math.round(n * 60) });
            }}
          >
            <h3 className="text-base font-bold text-slate-900 dark:text-white">{editing.kind === "steps" ? "Passos de hoje" : "Quanto você dormiu?"}</h3>
            <p className="mt-0.5 text-xs text-slate-500">
              {editing.kind === "steps" ? "Veja no app de saúde do celular ou no relógio e anote aqui." : "Em horas — ex.: 7,5 para 7h30."}
            </p>
            <input
              autoFocus
              inputMode="decimal"
              value={editing.value}
              onChange={(e) => setEditing({ ...editing, value: e.target.value })}
              placeholder={editing.kind === "steps" ? "Ex.: 6420" : "Ex.: 7,5"}
              className="mt-3 h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-lg font-bold outline-none focus:border-[var(--ai)] dark:border-slate-700 dark:bg-slate-800 dark:text-white"
            />
            {saveDay.error && <p className="mt-2 text-xs text-red-600">Não foi possível salvar.</p>}
            <button type="submit" disabled={saveDay.isPending} className="mt-3 w-full rounded-full ai-grad py-3 text-sm font-bold shadow-md">
              {saveDay.isPending ? "Salvando…" : "Salvar"}
            </button>
          </form>
        </div>
      )}

      {plansOpen && (
        <div className="absolute inset-0 z-50 flex flex-col bg-black/40" onClick={() => setPlansOpen(false)}>
          <div
            className="mt-auto max-h-[78%] overflow-hidden rounded-t-3xl bg-white dark:bg-slate-900"
            style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 pb-2 pt-4">
              <h3 className="text-base font-bold text-slate-900 dark:text-white">Seus treinos</h3>
              <button
                type="button"
                onClick={() => {
                  setPlansOpen(false);
                  ask("Monta um treino novo para mim: ");
                }}
                className="text-sm font-semibold ai-text"
              >
                + Novo
              </button>
            </div>
            <div className="max-h-[62vh] space-y-3 overflow-y-auto px-4 pb-5">
              {!h?.plans.length && <p className="py-6 text-center text-sm text-slate-500">Nenhum treino ainda. Peça ao personal para montar.</p>}
              {h?.plans.map((p) => (
                <div key={p.id} className="rounded-2xl border border-slate-100 p-3 dark:border-slate-800">
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-slate-900 dark:text-white">
                        {p.name}
                        {p.id === plan?.id && <span className="ml-2 rounded-full ai-soft px-2 py-0.5 text-[10px] font-bold ai-text">hoje</span>}
                      </div>
                      <div className="text-xs text-slate-500">
                        {p.weekdays.length ? p.weekdays.map((d) => WEEKDAYS[d]).join(", ") : "qualquer dia"}
                        {p.durationMin ? ` · ${p.durationMin} min` : ""}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setPlansOpen(false);
                        ask(`Altera o treino "${p.name}": `);
                      }}
                      className="rounded-full border ai-border px-2.5 py-0.5 text-[11px] font-semibold ai-text"
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      aria-label="Excluir treino"
                      onClick={() => removeWorkout.mutate(p.id)}
                      className="grid h-7 w-7 place-items-center rounded-full text-slate-400 hover:bg-red-50 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <ul className="mt-2 space-y-1">
                    {p.exercises.map((e, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                        <span>{exerciseEmoji(e.name)}</span>
                        <span className="flex-1 truncate">{e.name}</span>
                        <span className="text-xs font-semibold text-slate-500">{exerciseDetail(e)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({
  icon,
  iconBg,
  label,
  value,
  bar,
  barClass,
  foot,
  onClick,
}: {
  icon: React.ReactNode;
  iconBg: string;
  label: string;
  value: React.ReactNode;
  bar: number;
  barClass: string;
  foot: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-2xl bg-white p-2.5 text-left shadow-[0_8px_22px_-16px_rgba(15,23,42,0.55)] transition active:scale-95 dark:bg-slate-900"
    >
      <div className="flex items-center gap-1.5">
        <span className={cn("grid h-8 w-8 shrink-0 place-items-center rounded-full", iconBg)}>{icon}</span>
        <span className="text-[11.5px] text-slate-500">{label}</span>
      </div>
      <div className="mt-1 truncate text-[17px] font-extrabold tabular-nums text-slate-900 dark:text-white">{value}</div>
      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
        <div className={cn("h-full rounded-full", barClass)} style={{ width: `${Math.max(bar ? 4 : 0, bar)}%` }} />
      </div>
      <div className="mt-1 truncate text-[10.5px] font-semibold text-slate-500">{foot}</div>
    </button>
  );
}

function Macro({ label, value, goal, color }: { label: string; value: number; goal: number; color: string }) {
  return (
    <div className="mb-2 last:mb-0">
      <div className="flex items-center justify-between text-[11px]">
        <span className="font-semibold text-slate-600 dark:text-slate-300">{label}</span>
        <span className="font-bold tabular-nums text-slate-800 dark:text-slate-100">{value} g</span>
      </div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
        <div className={cn("h-full rounded-full", color)} style={{ width: `${Math.max(value ? 4 : 0, pct(value, goal))}%` }} />
      </div>
    </div>
  );
}

function EmptyCard({ emoji, title, text, action, onAction }: { emoji: string; title: string; text: string; action: string; onAction: () => void }) {
  return (
    <div className="flex flex-col items-center rounded-3xl border border-dashed ai-border bg-white/70 px-5 py-6 text-center dark:bg-slate-900/60">
      <span className="grid h-12 w-12 place-items-center rounded-2xl ai-soft text-2xl">{emoji}</span>
      <div className="mt-2 font-bold text-slate-800 dark:text-slate-100">{title}</div>
      <p className="mt-1 text-[13px] text-slate-500">{text}</p>
      <button type="button" onClick={onAction} className="mt-3 rounded-full ai-grad px-4 py-2 text-xs font-bold shadow-md">
        {action}
      </button>
    </div>
  );
}
